import { useEffect, useState, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { onAuthStateChanged, User } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from '../config/firebase';
import AuthPageLayout from '../components/AuthPageLayout';
import LoadingSpinner from '../components/LoadingSpinner';
import PartnerConnectIntro from '../components/PartnerConnectIntro';
import ScopeList from '../components/ScopeList';
import { useOAuthAuthorizeSessionPublic } from '../hooks/useOAuthAuthorizeSessionPublic';

type PageState = 'loading' | 'login_required' | 'consent' | 'processing' | 'error';

export default function ConsentPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get('session');

  const {
    session,
    loading: sessionLoading,
    error: sessionError,
  } = useOAuthAuthorizeSessionPublic(sessionId);

  const [pageState, setPageState] = useState<PageState>('loading');
  const [user, setUser] = useState<User | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!sessionId) {
      setError('Missing session parameter.');
      setPageState('error');
      return;
    }

    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      setUser(firebaseUser);
      if (firebaseUser) {
        setPageState('consent');
      } else {
        setPageState('login_required');
      }
    });

    return () => unsubscribe();
  }, [sessionId]);

  useEffect(() => {
    if (pageState === 'login_required' && sessionId) {
      navigate(`/login?session=${sessionId}&mode=signin`, { replace: true });
    }
  }, [pageState, sessionId, navigate]);

  useEffect(() => {
    if (sessionError) {
      setError(sessionError);
      setPageState('error');
    }
  }, [sessionError]);

  const handleAllow = useCallback(async () => {
    if (!sessionId) return;
    setPageState('processing');
    try {
      const completeAuth = httpsCallable<{ sessionId: string }, { redirectUrl: string }>(
        functions,
        'oauthAuthorizeCompleteV2'
      );
      const result = await completeAuth({ sessionId });
      // OAuth partner redirect_uri is NOT a web redirectUrl — it is validated server-side
      // against the partner's registered redirect URIs. Do NOT pass it through
      // sanitizeRedirectUrl / isSafeReturnTarget (those guard first-party app hosts only).
      window.location.href = result.data.redirectUrl;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Authorization failed.';
      setError(message);
      setPageState('error');
    }
  }, [sessionId]);

  const handleDeny = useCallback(async () => {
    if (!sessionId) return;
    setPageState('processing');
    try {
      const denyAuth = httpsCallable<{ sessionId: string }, { redirectUrl: string }>(
        functions,
        'oauthAuthorizeDenyV2'
      );
      const result = await denyAuth({ sessionId });
      // OAuth partner redirect_uri is backend-validated against registered partner URLs.
      // Do NOT sanitize as a web redirectUrl.
      window.location.href = result.data.redirectUrl;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to deny authorization.';
      setError(message);
      setPageState('error');
    }
  }, [sessionId]);

  // Error / missing-session must render BEFORE any loading guard, otherwise a
  // missing ?session= param is masked as an infinite spinner (DEV-463).
  if (!sessionId || pageState === 'error' || (!sessionLoading && !session)) {
    return (
      <AuthPageLayout
        title='Something went wrong'
        subtitle={
          !sessionId
            ? 'Missing session parameter.'
            : error || sessionError || 'An unexpected error occurred.'
        }
      >
        <p className='text-sm text-center text-gray-600 dark:text-gray-400'>
          Close this window and start again from your partner app.
        </p>
      </AuthPageLayout>
    );
  }

  // login_required briefly shows a spinner while the effect navigates to /login.
  if (pageState === 'login_required') {
    return (
      <AuthPageLayout title='Connect your accounts' subtitle='Preparing your connection...'>
        <LoadingSpinner />
      </AuthPageLayout>
    );
  }

  if (sessionLoading || !session || pageState === 'loading') {
    return (
      <AuthPageLayout
        title='Connect your accounts'
        subtitle='Preparing your connection...'
        hideTopLogo
      >
        <LoadingSpinner />
      </AuthPageLayout>
    );
  }

  return (
    <AuthPageLayout
      title='Authorize access'
      subtitle={
        <>
          Review what <strong>{session.partner.displayName}</strong> can access
          {user?.displayName
            ? ` on your 6Tool account (${user.displayName})`
            : ' on your 6Tool account'}
          .
        </>
      }
      hideTopLogo
    >
      <PartnerConnectIntro partner={session.partner} />

      <ScopeList scopes={session.scope} />

      <div className='mt-8 space-y-3'>
        <button
          type='button'
          onClick={handleAllow}
          disabled={pageState === 'processing'}
          className='w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transition-all duration-200'
        >
          {pageState === 'processing' ? 'Authorizing...' : 'Allow'}
        </button>
        <button
          type='button'
          onClick={handleDeny}
          disabled={pageState === 'processing'}
          className='w-full py-3 px-4 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-lg text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 transition-colors'
        >
          Deny
        </button>
      </div>

      {session.partner.privacyPolicyUrl ? (
        <p className='mt-6 text-xs text-center text-gray-500 dark:text-gray-400'>
          By allowing, you agree to {session.partner.displayName}&apos;s{' '}
          <a
            href={session.partner.privacyPolicyUrl}
            target='_blank'
            rel='noopener noreferrer'
            className='underline hover:text-gray-600 dark:hover:text-gray-300'
          >
            Privacy Policy
          </a>
          . You can disconnect at any time in your 6Tool settings.
        </p>
      ) : null}
    </AuthPageLayout>
  );
}
